import React from "react";
import { FileDownloadOutlined } from "@mui/icons-material";
import { Button } from "@mui/material";

import MultiselectAndChipsWithSearch from "modules/common/components/forms/inputs/multiSelectAndChipsWithSearch";
import Loading from "modules/common/components/loading";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { IdNamePair, IMultiSelectItem } from "modules/common/models";
import HoverText from "modules/documents/components/action-buttons/hoverText";
import { getAndDownloadAudienceUsers } from "./helpers/commonSMSFunctions";

interface IToAudienceSelectionProps {
    isFetching: boolean;
    currentUsers: number;
    totalAudienceUsers: number;
    audienceIds: string[];
    sendToAllUsers: boolean;
    filterByOptedIn: boolean;
    fileName: string;

    emptyChipText: string;
    disableOptions: boolean;
    audienceFooter: React.JSX.Element;
    defaultOptions: IMultiSelectItem<IdNamePair<string, string>>[];
    selectedOptions: IMultiSelectItem<IdNamePair<string, string>>[];
    onClose: () => void;
    onChange: (audience: IdNamePair<string, string>[]) => void;
    onChipRemove: (items: IdNamePair<string, string>[]) => void;
}

const ToAudienceSelection: React.FC<IToAudienceSelectionProps> = ({
    isFetching,
    currentUsers,
    totalAudienceUsers,
    audienceIds,
    sendToAllUsers,
    filterByOptedIn = true,
    fileName,

    emptyChipText,
    disableOptions,
    audienceFooter,
    defaultOptions,
    selectedOptions,
    onChange,
    onClose,
    onChipRemove
}) => {

    const [fetchingCSV, setFetchingCSV] = React.useState(false);
    const { setSuccessMessage, setErrorMessage } = useSetSnackbarMessages();

    const getAudienceLabel = () => {
        return <div style={{display: "flex"}}>
            {isFetching
                ? <Loading size={15}/> 
                : <span>{currentUsers.toLocaleString()} of {totalAudienceUsers.toLocaleString()}</span>
            }
            <span style={{alignContent: "space-around"}}>&nbsp;employee{totalAudienceUsers === 1 ? "" : "s"}</span>
        </div>;
    };

    const downloadAudienceUsers = async () => {        

        try {
            setFetchingCSV(true);
            return await getAndDownloadAudienceUsers(audienceIds, sendToAllUsers, filterByOptedIn, fileName, setSuccessMessage, setErrorMessage);
        }
        finally {
            setFetchingCSV(false);
        }
    }

    return (
        <React.Fragment>
            <div className="to-audience-selection-sms-container">
                <div className="internal-title">
                    To
                </div>
                <HoverText label={getAudienceLabel()} style={{alignSelf: "center"}}>
                    Only employees who are {filterByOptedIn ? "" : "not "} opted-in to receive SMS notifications, alerts and messages are counted.
                </HoverText>
                <Button 
                    style={{marginLeft: "auto"}} 
                    disabled={fetchingCSV || currentUsers === 0 || totalAudienceUsers === 0}
                    onClick={downloadAudienceUsers}
                    startIcon={<FileDownloadOutlined />}
                >
                    Download CSV
                </Button>
            </div>

            <div style={{display: "flex", flexDirection: "row"}}>
                <div style={{minWidth: 100}}/>
                <MultiselectAndChipsWithSearch
                    defaultOptions={defaultOptions}
                    selectedOptions={selectedOptions}
                    onChange={onChange}
                    onChipRemove={onChipRemove}
                    chipListStyle={{emptyChipStyling: {show: true, contents: emptyChipText}}}
                    buttonText="Add/Edit Audiences"
                    footerComponent={audienceFooter}
                    calloutContainerStyle={{ width: "fit-content", minWidth: "316px" }}
                    searchPlaceholder="Search for audiences"
                    nestingAllowed={false}
                    buttonCssStyle={{ paddingLeft: 0 }}
                    disableOptions={disableOptions}
                    calloutFooterStyle={{ justifyContent: "center" }}
                    buttonId="add-edit-audiences-button"
                    onClose={onClose}
                />
            </div>
        </React.Fragment>
    )
}

export default (ToAudienceSelection);